// Sends a 6-digit password reset code via our own email infrastructure.
// Responds OK even if no account exists, so attackers can't probe for users.
export async function sendPasswordResetCode(email: string) {
  const resp = await fetch('/api/email/send-password-reset-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await safeJson(resp);
  if (!resp.ok) {
    throw new Error(data?.error || 'Failed to send reset code');
  }
  return data;
}

// Sends a 6-digit email verification code via our own email infrastructure
// (Resend → SMTP fallback). Replaces Firebase's hosted-link flow.
export async function sendVerificationCode(email: string, name?: string) {
  const resp = await fetch('/api/email/send-verification-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, name }),
  });
  const data = await safeJson(resp);
  if (!resp.ok) {
    throw new Error(data?.error || 'Failed to send code');
  }
  return data;
}

// Verifies the 6-digit code. If uid is supplied, the backend also marks the
// Firebase Auth user as emailVerified via the Admin SDK.
export async function verifyEmailCode(email: string, code: string, uid?: string) {
  const resp = await fetch('/api/email/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, uid }),
  });
  const data = await safeJson(resp);
  if (!resp.ok) {
    throw new Error(data?.error || 'Failed to verify code');
  }
  return data;
}

export async function sendWelcomeEmail(uid: string, email: string, name?: string, role: 'buyer' | 'seller' = 'buyer') {
  const resp = await fetch('/api/email/send-welcome', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uid, email, name, role }),
  });
  if (!resp.ok) {
    const err = await safeJson(resp);
    throw new Error(err?.error || 'Failed to send welcome email');
  }
  return resp.json();
}

export async function adminBroadcastEmail(payload: { scope: 'all' | 'buyers' | 'sellers' | 'ids' | 'emails'; subject: string; html?: string; text?: string; targetUserIds?: string[]; targetIds?: string[]; recipientEmails?: string[]; }) {
  const resp = await fetch('/api/email/admin-broadcast', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      targetUserIds: payload.targetUserIds || payload.targetIds,
    }),
  });
  if (!resp.ok) {
    const err = await safeJson(resp);
    const msg = String(err?.error || 'Failed to send admin email');
    const details = err?.details ? String(err.details) : '';
    throw new Error(details ? `${msg}: ${details}` : msg);
  }
  return resp.json();
}

export async function sellerProductAddedEmail(email: string, productName: string, productUrl?: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/seller-product-added', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, productName, productUrl })
    });
    if (!resp.ok) return false; const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export interface TicketPurchaseEmailPayload {
  buyerEmail: string;
  buyerName: string;
  sellerEmail?: string;
  sellerName?: string;
  eventTitle: string;
  eventDate?: string;
  venue?: string;
  tierName?: string;
  quantity: number;
  unitPrice: number;
  total: number;
  sellerAmount?: number;
  codes: string[];
}

// Emails the buyer their e-ticket(s) and notifies the seller of the sale.
export async function ticketPurchasedEmail(payload: TicketPurchaseEmailPayload): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/ticket-purchased', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) return false; const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function orderReceivedEmail(sellerEmail: string, orderId: string, buyerName?: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/order-received', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sellerEmail, orderId, buyerName })
    });
    if (!resp.ok) return false; const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function orderDeliveredEmail(orderId: string, sellerEmail?: string, buyerEmail?: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/order-delivered', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId, sellerEmail, buyerEmail })
    });
    if (!resp.ok) return false; const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function sellerVerifiedEmail(email: string, sellerName?: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/seller-verified', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, sellerName })
    });
    if (!resp.ok) return false; const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function sellerProEmail(email: string, sellerName?: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/seller-pro', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, sellerName })
    });
    if (!resp.ok) return false; const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function newMessageEmail(payload: {
  recipientEmail: string;
  senderName: string;
  preview?: string;
  conversationId?: string;
  type?: 'text' | 'image' | 'audio' | 'system';
}): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/new-message', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) return false;
    const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function sellerApprovedEmail(email: string, sellerName?: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/seller-approved', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, sellerName })
    });
    if (!resp.ok) return false;
    const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

export async function sellerRejectedEmail(email: string, sellerName: string | undefined, reason: string): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/seller-rejected', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, sellerName, reason })
    });
    if (!resp.ok) return false;
    const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch { return false; }
}

// Sends a test email via Resend to confirm the integration is working.
// If `to` is omitted, sends to the admin notification list.
export async function sendTestEmail(to?: string): Promise<{ ok: boolean; id?: string | null; error?: string; recipients?: string[]; from?: string; provider?: 'resend' | 'smtp' }> {
  try {
    const resp = await fetch('/api/email/email-test', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: to || '' })
    });
    const data = await resp.json().catch(() => null);
    if (!resp.ok) return { ok: false, error: data?.error || `HTTP ${resp.status}` };
    return { ok: !!(data && data.ok), id: data?.id ?? null, recipients: data?.recipients, from: data?.from, provider: data?.provider };
  } catch (e: any) {
    return { ok: false, error: String(e?.message || e) };
  }
}

// Fire-and-forget admin notification. Returns true on success, false on any error
// (callers should not await it as a blocker for user actions).
export async function notifyAdmins(payload: {
  event: string;
  subject: string;
  text?: string;
  html?: string;
  meta?: Record<string, any>;
}): Promise<boolean> {
  try {
    const resp = await fetch('/api/email/admin-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) return false;
    const data = await resp.json().catch(() => null);
    return !!(data && data.ok === true);
  } catch {
    return false;
  }
}

async function safeJson(resp: Response) {
  try { return await resp.json(); } catch { return null; }
}
